import { CSSProperties, ReactNode, useEffect } from 'react';
import classnames from 'classnames';

import style from './RightMenu.module.scss';
import '@components/mixins/mixin.scss';

import { ClickOutsideMask } from '@components/ClickOutsideMask';
import { ClickOutsideAlerter } from '@components/ClickOutsideAlerter';
import { ScrollBar } from '@components/ScrollBar';
import { Icon, IconType } from '@components/Icon';
import { removeNilKeys } from '@utils/removeNiKeys';

import { CommonTestAttributes } from 'chaika-test-attributes';
import { RightMenuPortal } from '@components/RightMenu/RightMenuPortal';

export interface RightMenuProps {
    isShowing?: boolean;
    isCloseButtonHidden?: boolean;
    renderOnOpen?: boolean;
    children: ReactNode | ReactNode[];
    customWidth?: boolean;
    fullScreenHeight?: boolean;
    stickyButtonsLayout?: boolean;
    right?: number;
    withoutMask?: boolean;
    closeOnEscOnly?: boolean;
    zIndex?: number;
    onClickOutside?(): void;
    closeByEsc?(): void;
}

export function RightMenu({
    children,
    isShowing = true,
    renderOnOpen = true,
    isCloseButtonHidden = false,
    customWidth,
    right,
    zIndex,
    withoutMask,
    closeOnEscOnly,
    fullScreenHeight,
    stickyButtonsLayout,
    onClickOutside,
    closeByEsc,
}: RightMenuProps) {
    const additionalStyle: CSSProperties = removeNilKeys({
        right,
        zIndex,
    });

    useEffect(() => {
        if (!closeByEsc) {
            return undefined;
        }

        function handleEscKeydown(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                closeByEsc?.();
            }
        }

        document.addEventListener('keydown', handleEscKeydown);

        return () => document.removeEventListener('keydown', handleEscKeydown);
    }, [closeByEsc]);

    return (
        <RightMenuPortal>
            <ClickOutsideAlerter onClickOutside={withoutMask && !closeOnEscOnly ? onClickOutside : undefined}>
                <div
                    className={classnames([style.root, customWidth && style.root_customWidth])}
                    style={additionalStyle}
                >
                    {!withoutMask && !closeOnEscOnly && isShowing && (
                        <ClickOutsideMask onClickOutside={onClickOutside} />
                    )}
                    <div className={classnames([style.content, isShowing && style.content_showing])}>
                        <ScrollBar>
                            <>
                                <div
                                    className={classnames([
                                        style.contentWrapper,
                                        stickyButtonsLayout && style.stickyButtonsLayout,
                                        fullScreenHeight && style.fullScreenHeight,
                                    ])}
                                >
                                    {renderOnOpen ? children : ''}
                                </div>
                                {onClickOutside && (
                                    <div
                                        className={classnames([
                                            'closeIconWrapper',
                                            style.closeIconWrapper,
                                            isCloseButtonHidden && style.hiddenCloseButton,
                                        ])}
                                        data-test={CommonTestAttributes.RightMenuCrossIcon}
                                        data-testid={CommonTestAttributes.RightMenuCrossIcon}
                                        onClick={closeByEsc ?? onClickOutside}
                                    >
                                        <span className={style.closeIconHint}>ESC</span>
                                        <Icon type={IconType.CROSS} />
                                    </div>
                                )}
                            </>
                        </ScrollBar>
                    </div>
                </div>
            </ClickOutsideAlerter>
        </RightMenuPortal>
    );
}
